import { memo } from 'react'
import { safeButtonHaptic } from '../../lib/safeTelegram'

/**
 * Figma TelegramUI Component — iOS header + glass buttons
 *
 * Props:
 * - element: "close" | "back" | "menu" | "none" (default: "none")
 * - onClose: обработчик для кнопки "Закрыть"
 * - onBack: обработчик для кнопки "Назад"
 * - onMenu: обработчик для кнопки меню
 * - showHeader: показывать iOS header (default: true)
 * - className: дополнительные классы
 *
 * Размеры из Figma:
 * - iOS header: 393×89px (верхняя часть с временем, батареей)
 * - Glass buttons: высота ~44px, backdrop-blur 4.977px
 * - Position: absolute top-0, z-index высокий
 */

function TelegramUI({
  element = 'none',
  onClose,
  onBack,
  onMenu,
  showHeader = true,
  className = '',
}) {
  const handleClick = (handler) => {
    safeButtonHaptic()
    handler?.()
  }

  // Glass button base styles
  const glassButtonStyles = `
    inline-flex items-center gap-2
    h-[44px] px-4
    rounded-[999px]
    bg-[var(--figma-overlay)]
    backdrop-blur-[4.977px]
    border border-[var(--figma-border-10)]
    font-[510] text-[16px] leading-[22px] tracking-[-0.01em]
    text-[var(--figma-text)]
    transition-all duration-[160ms]
    active:scale-[0.96]
    touch-action-manipulation
  `

  return (
    <div className={`absolute top-0 left-0 right-0 z-50 ${className}`}>
      {/* iOS Header — декоративный элемент */}
      {showHeader && (
        <div className="h-[89px] flex items-start justify-between px-6 pt-3">
          {/* Time */}
          <div className="text-[15px] font-[590] text-[var(--figma-text)] pt-1">
            {new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
          </div>

          {/* Status icons (Signal, WiFi, Battery) — декоративные */}
          <div className="flex items-center gap-1.5 pt-1">
            <svg width="18" height="12" viewBox="0 0 18 12" fill="none" className="opacity-80">
              <path d="M16.5 0C17.3284 0 18 0.671573 18 1.5V10.5C18 11.3284 17.3284 12 16.5 12H1.5C0.671573 12 0 11.3284 0 10.5V1.5C0 0.671573 0.671573 0 1.5 0H16.5ZM16.5 1H1.5C1.22386 1 1 1.22386 1 1.5V10.5C1 10.7761 1.22386 11 1.5 11H16.5C16.7761 11 17 10.7761 17 10.5V1.5C17 1.22386 16.7761 1 16.5 1Z" fill="white"/>
              <path d="M15 3C15 2.44772 15.4477 2 16 2C16.5523 2 17 2.44772 17 3V9C17 9.55228 16.5523 10 16 10C15.4477 10 15 9.55228 15 9V3Z" fill="white" fillOpacity="0.4"/>
              <rect x="2" y="3" width="13" height="6" rx="1" fill="white"/>
            </svg>
          </div>
        </div>
      )}

      {/* Glass Buttons */}
      {element !== 'none' && (
        <div className="px-4 pb-4">
          {element === 'close' && (
            <button
              type="button"
              onClick={() => handleClick(onClose)}
              className={glassButtonStyles}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Закрыть</span>
            </button>
          )}

          {element === 'back' && (
            <button
              type="button"
              onClick={() => handleClick(onBack)}
              className={glassButtonStyles}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12 15L7 10L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Назад</span>
            </button>
          )}

          {element === 'menu' && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleClick(onMenu)}
                className={glassButtonStyles}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M6 10L10 6L14 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                type="button"
                className={glassButtonStyles}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="4" r="1.5" fill="currentColor"/>
                  <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
                  <circle cx="10" cy="16" r="1.5" fill="currentColor"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default memo(TelegramUI)
